// Feature Codes -- read-only "what can I dial" page for every department member (portal-phones-ux).
// The editable System -> Dial Codes page is system-admin only and shows raw keys; this one shows
// friendly labels, how to use each code, and the department's parking spots.
//
// Parking: the supported flow today is "transfer the call to a parking spot number to park it,
// dial the same number to pick it back up". The *0 / *1<orbit> codes are deliberately not listed
// (*1<orbit> retrieve is broken while it is being fixed).

import { useQuery } from '@tanstack/react-query';
import { Hash, ParkingSquare } from 'lucide-react';
import { api } from '../lib/api';
import { getScopedDomain } from '../lib/tenantScope';
import { describeError } from '../lib/portalUx';
import ScopeBanner from '../components/ScopeBanner';

export default function FeatureCodes({ embedded = false }: { embedded?: boolean }) {
  const me = api.me();
  // End users (webclient) always see their own department; admins follow the Viewing selector.
  const domain = embedded ? me?.domain_uuid : getScopedDomain(me?.domain_uuid);
  const q = useQuery({
    queryKey: ['feature-codes', domain],
    queryFn: () => api.devices.featureCodes(domain as string),
    enabled: !!domain,
  });

  return (
    <div className={`space-y-4 ${embedded ? 'p-5 overflow-auto h-full' : ''}`}>
      <h1 className="text-2xl font-semibold flex items-center gap-2"><Hash /> Feature Codes</h1>
      {!embedded && <ScopeBanner what="The Feature Codes page" />}
      <p className="text-sm text-textSecondary">Codes you can dial from any phone or app in your department.</p>

      {q.isLoading && <div className="text-sm text-textSecondary">Loading…</div>}
      {q.error && (
        <div className="card p-4 border border-danger/50 text-sm text-danger">
          Could not load feature codes: {describeError(q.error)?.message || String(q.error)}
        </div>
      )}

      {q.data && (
        <>
          <div className="card space-y-3">
            <h2 className="font-semibold flex items-center gap-2"><ParkingSquare size={16} /> Call parking</h2>
            {q.data.parking.ranges.length > 0 && (
              <div className="text-sm">
                Your parking spots: <span className="font-mono">{q.data.parking.ranges.join(', ')}</span>
              </div>
            )}
            <ul className="text-sm list-disc pl-5 space-y-1">
              {q.data.parking.usage.map((u, i) => <li key={i}>{u}</li>)}
            </ul>
          </div>

          <div className="card p-0 overflow-x-auto">
            <table className="table">
              <thead><tr><th className="w-56">Feature</th><th className="w-28">Code</th><th>How to use it</th></tr></thead>
              <tbody>
                {q.data.codes.map(c => (
                  <tr key={c.key}>
                    <td className="font-medium">{c.label}</td>
                    <td className="font-mono">{c.code}</td>
                    <td className="text-sm space-y-0.5">{c.usage.map((u, i) => <div key={i}>{u}</div>)}</td>
                  </tr>
                ))}
                {q.data.codes.length === 0 && (
                  <tr><td colSpan={3} className="text-center py-6 text-textSecondary">No feature codes are configured for this department.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
